<template>
  <subscription-guard>
    <div class="pil-wrap">

      <!-- header -->
      <div class="pil-header">
        <div class="pil-header-left">
          <h1 class="pil-title">Find influencers</h1>
          <p class="pil-subtitle">Browse and invite influencers to your campaigns.</p>
        </div>
        <div class="pil-header-right">
          <button
            class="pil-fav-btn"
            :class="{ 'pil-fav-btn--active': filters.favorite }"
            @click="toggleFavourite(!filters.favorite)"
          >
            <i :class="filters.favorite ? 'fas fa-heart' : 'fal fa-heart'" style="margin-right:6px;"></i>
            Only favourites
          </button>
          <button
            class="pil-fav-btn pil-note-btn"
            :class="{ 'pil-note-btn--active': filters.has_note }"
            @click="toggleHasNote(!filters.has_note)"
          >
            <i :class="filters.has_note ? 'fas fa-sticky-note' : 'fal fa-sticky-note'" style="margin-right:6px;"></i>
            Only with notes
          </button>
        </div>
      </div>

      <!-- filter bar -->
      <div class="pil-filters hidden-xs-only">
        <div class="pil-search-wrap">
          <Icon name="search" :size="14" class="pil-search-icon" />
          <input
            v-model="search"
            class="pil-search"
            placeholder="Search influencers…"
            @input="searchMethod"
          />
        </div>

        <div class="pil-chips-row">
          <v-select
            placeholder="Channel"
            :items="channelItems"
            return-object
            v-model="filters.channel"
            @blur="saveFilters"
            class="pil-chip-select"
            :class="{ 'is-active': filters.channel }"
            hide-details dense outlined
          >
            <template v-slot:selection="{ item }">
              <span>{{ item.text }}</span>
            </template>
          </v-select>

          <v-select
            placeholder="Gender"
            :items="genderItems"
            return-object
            v-model="filters.gender"
            @blur="saveFilters"
            class="pil-chip-select"
            :class="{ 'is-active': filters.gender }"
            hide-details dense outlined
          >
            <template v-slot:selection="{ item }">
              <span>{{ item.text }}</span>
            </template>
          </v-select>

          <v-select
            placeholder="Categories"
            :items="formattedCategories"
            v-model="filters.category_ids"
            :return-object="false"
            multiple
            @blur="saveFilters"
            class="pil-chip-select"
            :class="{ 'is-active': filters.category_ids && filters.category_ids.length }"
            hide-details dense outlined
          >
            <template v-slot:selection="{ index }">
              <span v-if="index === 0">
                Categories{{ filters.category_ids && filters.category_ids.length ? ` (${filters.category_ids.length})` : '' }}
              </span>
            </template>
          </v-select>

          <v-select
            placeholder="Age"
            :items="ageItems"
            v-model="filters.age_ranges"
            :return-object="false"
            multiple
            @blur="saveFilters"
            class="pil-chip-select"
            :class="{ 'is-active': filters.age_ranges && filters.age_ranges.length }"
            hide-details dense outlined
          >
            <template v-slot:selection="{ index }">
              <span v-if="index === 0">
                Age{{ filters.age_ranges && filters.age_ranges.length ? ` (${filters.age_ranges.length})` : '' }}
              </span>
            </template>
          </v-select>

          <v-select
            v-model="mySelectedCountries"
            placeholder="Countries"
            :items="countries"
            item-text="name"
            item-value="iso_3166_2"
            :loading="!countries.length"
            multiple
            @change="updateSelectedCountries"
            class="pil-chip-select"
            :class="{ 'is-active': mySelectedCountries && mySelectedCountries.length }"
            hide-details dense outlined
          >
            <template v-slot:selection="{ index }">
              <span v-if="index === 0">
                Countries{{ mySelectedCountries && mySelectedCountries.length ? ` (${mySelectedCountries.length})` : '' }}
              </span>
            </template>
          </v-select>

          <v-menu
            v-model="followersRange"
            :close-on-content-click="false"
            :nudge-width="280"
            offset-y
            @input="setRange"
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="pil-chip-btn"
                :class="{ 'pil-chip-btn--accent': range[0] !== 0 || range[1] !== 5 }"
              >
                {{ followerLabel }}
                <v-icon small style="margin-left:4px; font-size:10px">fal fa-angle-down</v-icon>
              </div>
            </template>
            <v-card style="min-width:360px; border-radius:16px !important; overflow:hidden; box-shadow:0 8px 32px rgba(0,0,0,0.14) !important; border:1px solid var(--line,#eaecf0);">
              
              <!-- Header -->
              <div style="display:flex; align-items:center; justify-content:space-between; padding:18px 22px 0;">
                <span style="font-size:11px; font-weight:700; color:var(--ink-3,#aaa); text-transform:uppercase; letter-spacing:0.08em;">Follower count</span>
                <button 
                  v-if="range[0] !== 0 || range[1] !== 5" 
                  @click.stop="resetFollowerRange"
                  style="font-size:12px; color:#e05540; background:none; border:none; cursor:pointer; padding:0; font-weight:600;">
                  Reset
                </button>
              </div>

              <!-- Current selection -->
              <div style="padding:6px 22px 0; display:flex; align-items:baseline; gap:6px;">
                <span style="font-size:26px; font-weight:800; color:var(--ink-1,#1a1a1a); letter-spacing:-0.5px;">
                  {{ range[0] === 0 && range[1] === 5 ? 'Any' : followerLabel }}
                </span>
                <span style="font-size:13px; color:var(--ink-3,#aaa);">followers</span>
              </div>

              <!-- Slider -->
              <div style="padding:0 18px;">
                <v-range-slider
                  v-model="range"
                  min="0"
                  max="5"
                  ticks="always"
                  tick-size="3"
                  :tick-labels="ticksRange"
                  height="80"
                  color="#e05540"
                  track-color="#eaecf0"
                ></v-range-slider>
              </div>

              <!-- Footer -->
              <div style="padding:4px 22px 18px; display:flex; justify-content:flex-end;">
                <button
                  @click="applyFollowerRange"
                  style="padding:9px 24px; background:#e05540; color:#fff; border:none; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; transition:opacity 0.15s;"
                  onmouseover="this.style.opacity='0.88'"
                  onmouseout="this.style.opacity='1'"
                >Apply</button>
              </div>

            </v-card>
          </v-menu>

          <div class="pil-chip-btn pil-chip-btn--accent" @click="advancedDialog = true">
            <i class="fal fa-sliders-h" style="font-size:12px"></i> Advanced search
          </div>

          <div
            v-if="isFilterChips"
            class="pil-chip-btn pil-chip-btn--remove"
            @click="cleanFilters(false)"
          >
            <i class="fal fa-times" style="font-size:12px"></i> Remove filters
          </div>

          <div class="pil-sort-wrap">
            <span class="pil-sort-label">Sorting</span>
            <v-select
              v-model="selectedSorting"
              :items="sortingItems"
              item-text="text"
              return-object
              class="pil-chip-select"
              hide-details dense outlined
              @change="saveFilters"
            >
              <template v-slot:selection="data">
                <span>{{ data.item.text }}</span>
              </template>
            </v-select>
          </div>
        </div>
      </div>

      <!-- grid -->
      <div class="pil-grid" v-if="influencers.length > 0">
        <influencer-card
          :influencer="item"
          class="influncer-card"
          :ref="item.uuid"
          v-for="item of influencers"
          :key="item.uuid"
          show-note
          @avatar-click="showStatsModal(item)"
        >
          <template v-slot:footer>
            <v-btn small depressed
              style="flex:1; border-radius:8px; font-size:12px; font-weight:600; color:var(--ink-2,#555); background:var(--surface-2,#f5f5f5); text-transform:none; min-width:0;"
              @click="showStatsModal(item)">
              <i class="fal fa-chart-bar" style="margin-right:5px;font-size:11px;"></i>Media data
            </v-btn>
            <v-btn small depressed
              style="flex:1; border-radius:8px; font-size:12px; font-weight:600; color:#fff; background:#e05540; text-transform:none; min-width:0;"
              @click="selectedInfluencer = item; showCampaignInvitationDialog = true;">
              <i class="fal fa-user-plus" style="margin-right:5px;font-size:11px;"></i>Invite
            </v-btn>
          </template>
        </influencer-card>
      </div>

      <!-- skeletons -->
      <div class="pil-grid" v-else-if="skeletonArray.length > 0">
        <v-card v-for="(s, i) in skeletonArray" :key="i" class="influencer-skeleton">
          <div class="d-flex justify-center">
            <v-skeleton-loader class="rounded" height="150px" width="150px" type="image" />
          </div>
          <v-skeleton-loader type="list-item-avatar-two-line" />
          <v-skeleton-loader type="list-item-avatar-two-line" />
          <div class="influencer-skeleton-buttons mb-2">
            <v-skeleton-loader type="button" />
            <v-skeleton-loader type="button" />
          </div>
        </v-card>
      </div>

      <!-- empty -->
      <div v-else class="pil-empty">
        <img src="./../../assets/searchFailed.svg" style="width:220px;opacity:.6" />
        <p class="muted" style="margin-top:16px">No results for your search.</p>
      </div>

      <v-pagination
        v-if="totalPages && totalPages > 1 && influencers.length"
        v-model="page"
        class="my-2"
        :length="totalPages"
        :total-visible="7"
        @input="handlePageChange"
      />

      <!-- scroll to top FAB -->
      <v-btn
        v-if="influencers.length > 12 && isUserScrolling"
        class="hidden-xs-only"
        color="accent"
        fixed bottom right fab
        @click="scrollToTop()"
      >
        <v-icon color="white">fal fa-arrow-to-top</v-icon>
      </v-btn>

      <!-- mobile buttons -->
      <v-btn class="hidden-sm-and-up first-btn mobile-filter-btn" fab fixed bottom right small @click="showMobileSearch = true">
        <v-icon>fal fa-search</v-icon>
      </v-btn>
      <v-btn class="hidden-sm-and-up mobile-filter-btn" fab fixed bottom right small @click="showMobileFilters = true">
        <v-icon>fas fa-filter</v-icon>
      </v-btn>

      <!-- dialogs -->
      <advanced-filter-dialog
        v-model="advancedDialog"
        :filters="filters"
        @saved="saveAdvancedFilters($event)"
      />
      <influencer-dialog
        v-if="selectedInfluencer"
        v-model="showInfluencerDialog"
        :influencer="selectedInfluencer"
        @input="!$event ? (selectedInfluencer = null) : null"
        @change="influencerChange"
        @invite="handleInfluencerInvite($event)"
      />
      <bulk-invite-campaign-dialog
        v-model="showCampaignInvitationDialog"
        :selectedInfluencer="selectedInfluencer"
      />
      <filter-influencers-mobile
        v-model="showMobileFilters"
        :filters="filters"
        @saved="filters = $event"
        @saveFilters="saveFilters"
        :channelItems="channelItems"
        :genderItems="genderItems"
        :ageItems="ageItems"
        :ticksRange="ticksRange"
        :categories="formattedCategories"
        :range="range"
        :ticksNumberRange="ticksNumberRange"
      />
      <v-dialog v-model="showMobileSearch" max-width="350px">
        <v-card>
          <v-card-title>Search word</v-card-title>
          <v-card-text>
            <v-text-field v-model="search" prepend-inner-icon="search" placeholder="Search word" clearable class="custom-search" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="showMobileSearch = false">Undo</v-btn>
            <v-btn color="primary" @click="saveSearch()">Apply</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </subscription-guard>
</template>

<script>
import InfluencerCard from "../cards/InfluencerCard";
import AdvancedFilterDialog from "../dialogs/AdvancedFilterDialog";
import FilterInfluencersMobile from "@/components/common/filters/FilterInfluencersMobile";
import search_filters_url from "@/helpers/mixins/search_filters_url";
import SubscriptionGuard from "@/components/business/pages/SubscriptionGuard";
import InfluencerDialog from "../dialogs/InfluencerDialog";
import BulkInviteCampaignDialog from "@/components/common/dialogs/BulkInviteCampaignDialog";
import { mapActions, mapState, mapGetters } from "vuex";
import _ from "lodash";

export default {
  mixins: [search_filters_url],
  components: {
    BulkInviteCampaignDialog,
    FilterInfluencersMobile,
    SubscriptionGuard,
    InfluencerDialog,
    InfluencerCard,
    AdvancedFilterDialog
  },
  data: () => ({
    countries: [],
    mySelectedCountries: [],
    skeletonArray: [],
    followersRange: false,
    limit: 18,
    totalInfluencers: null,
    selectedSorting: { text: "Recommended", orderBy: "recommended", asc: true },
    search: "",
    showMobileFilters: false,
    showMobileActions: false,
    influencers: [],
    filters: {},
    loading: false,
    selectedInfluencer: null,
    showInfluencerDialog: false,
    showMobileSearch: false,
    advancedDialog: false,
    showCampaignInvitationDialog: false,
    totalPages: null,
    page: 1,
    range: [0, 5],
    cardWidth: { xs: 100, sm: 50, md: 33.33, lg: 33.33, xl: 33.33 },
    ageItems: [
      { text: "Under 18", min: 0, max: 18, value: "18" },
      { text: "18-24", min: 18, max: 24, value: "18-24" },
      { text: "25-29", min: 25, max: 29, value: "25-29" },
      { text: "30-34", min: 30, max: 34, value: "30-34" },
      { text: "35-39", min: 35, max: 39, value: "35-39" },
      { text: "40-44", min: 40, max: 44, value: "40-44" },
      { text: "45-50", min: 45, max: 50, value: "45-50" },
      { text: "Over 50", min: 50, max: 150, value: "50" }
    ],
    followerCountItems: [
      { text: '0 – 5K',     min: 0,      max: 5000   },
      { text: '5K – 10K',   min: 5000,   max: 10000  },
      { text: '10K – 25K',  min: 10000,  max: 25000  },
      { text: '25K – 50K',  min: 25000,  max: 50000  },
      { text: '50K – 100K', min: 50000,  max: 100000 },
      { text: '100K+',      min: 100000, max: null   },
    ],
    min: 0,
    max: 100000,
    ticksNumberRange: [0, 5000, 10000, 25000, 50000, 100000],
    ticksRange: ["0", "5K", "10K", "25K", "50K", "100K+"],
    categories: [],
    sortingItems: [
      { text: "Recommended", orderBy: "recommended", asc: true },
      { text: "Alphabetical A-Z", orderBy: "alphabetical", asc: true },
      { text: "Alphabetical Z-A", orderBy: "alphabetical", desc: true },
      { text: "Newest", orderBy: "newest", desc: true },
      { text: "Oldest", orderBy: "newest", asc: true },
      { text: "Followers high-low", orderBy: "followers", asc: false },
      { text: "Views high-low", orderBy: "views", asc: false }
    ],
    channelItems: [
      { text: "Instagram", id: "instagram" },
      { text: "Tiktok", id: "tiktok" }
    ],
    genderItems: [
      { text: "Male", id: "male" },
      { text: "Female", id: "female" }
    ],
    prevRouteName: null,
    isUserScrolling: false,
    handleDebouncedScroll: null
  }),
  computed: {
    ...mapGetters("core/auth", ["isAgency"]),
    ...mapState("core/auth", ["user"]),
    ...mapGetters("subscriptions", ["hasPermission"]),
    ...mapState("filters", ["selectedCountries"]),
    formattedCategories() {
      return this.categories.map(item => ({ text: item.display_name, value: Number(item.id) }));
    },
    isFilterChips() {
      const filters = this.cloneDeep(this.filters);
      return (
        !!Object.keys(filters).length &&
        ((filters.category_ids && filters.category_ids.length) ||
          (filters.channel && Object.keys(filters.channel).length) ||
          (filters.gender && Object.keys(filters.gender).length) ||
          (filters.age_ranges && filters.age_ranges.length) ||
          !!filters.max_followers ||
          !!filters.min_followers ||
          (filters.age_genders && filters.age_genders.length) ||
          (filters.city && filters.city.length) ||
          (filters.zipcode && filters.zipcode.length) ||
          (filters.cities && filters.cities.length))
      );
    },
    followerLabel() {
      if (this.range[0] === 0 && this.range[1] === 5) return 'Follower count';
      return `${this.ticksRange[this.range[0]]} – ${this.ticksRange[this.range[1]]}`;
    },
  },
  watch: {
    search: { handler: _.debounce(function() { this.saveFilters(); }, 500) },
    selectedSorting: {
      handler: _.debounce(function() {
        if (!this.selectedSorting) { delete this.filters.orderBy; delete this.filters.order; return; }
        this.filters.orderBy = this.selectedSorting.orderBy;
        this.filters.order = this.selectedSorting.asc ? "asc" : "desc";
        if (!this.selectedSorting.asc && !this.selectedSorting.desc) { delete this.filters.orderBy; delete this.filters.order; }
        this.saveFilters();
      }, 500)
    },
    selectedCountries: {
      handler: _.debounce(function() { this.filters.country_codes = this.selectedCountries; this.saveFilters(); }, 500)
    }
  },
  methods: {
    ...mapActions("core", ["getCountries"]),
    ...mapActions("core", ["getInfluencers", "getChannelCategories"]),
    ...mapActions("filters", ["setSelectedCountries"]),
    loadActiveCountries() {
      this.getCountries({ active: true }).then(
        countries => { this.countries = countries; this.updateSelectedCountries(); },
        error => { console.log(error); }
      );
    },
    updateSelectedCountries() { this.setSelectedCountries(this.mySelectedCountries); },
    handleScroll() { this.isUserScrolling = window.scrollY > 200; },
    toggleFavourite(value) { this.filters.favorite = value; this.saveFilters(); },
    toggleHasNote(value) { this.$set(this.filters, "has_note", value); this.saveFilters(); },
    searchMethod: _.debounce(function() { this.saveFilters(); }, 500),
    saveSearch() { this.filters.search = this.search; this.saveFilters(); this.search = null; this.showMobileSearch = false; },
    handlePageChange(value) { this.scrollToTop(); this.page = value; this.loadInfluencers(value); },
    saveAdvancedFilters(event) { this.filters = event; this.page = 1; this.loadInfluencers(this.page); },
    saveFilters() { this.page = 1; this.loadInfluencers(this.page); },
    influencerChange(event) {
      let item = this.influencers.find(item => item.uuid === event.uuid);
      item.is_favorite = event.value;
      this.$refs[item.uuid][0].updateFavorite(event.value);
    },
    showStatsModal(influencer) { this.selectedInfluencer = influencer; this.showInfluencerDialog = true; },
    addSkeletons() { let arr = []; for (let i = 0; i < 12; i++) arr.push({ uuid: "skeleton" }); this.skeletonArray = arr; },
    removeSkeletons() { this.skeletonArray = []; },
    removeChip(event) {
      let idx = null;
      if (event.item === "channel") delete this.filters["channel"];
      else if (event.item === "gender") delete this.filters["gender"];
      else if (event.item === "followers") { delete this.filters["min_followers"]; delete this.filters["max_followers"]; }
      else { idx = this.filters[event.item].findIndex(value => value === event.id); this.filters[event.item].splice(idx, 1); }
      this.cleanFilters(true);
    },
    loadInfluencers(page = 1) {
      if (this.loading) return null;
      this.influencers = [];
      this.addSkeletons();
      this.loading = true;
      this.filters.page = page;
      this.filters.search = this.search;
      this.filters.orderBy = this.selectedSorting.orderBy;
      this.filters.order = this.selectedSorting.asc ? "asc" : "desc";
      let filters = this.cloneDeep(this.filters);
      filters.limit = this.limit;
      filters.status = [1, 3];
      filters.page = page;
      const filteredAdvancedKeys = ["cities", "age_genders"];
      if (filters["age_ranges"]) {
        const arr = [];
        filters["age_ranges"].map(id => {
          const item = this.ageItems.find(age => age.value === id);
          if (item) arr.push({ min: item.min, max: item.max });
        });
        filters["age_ranges"] = arr;
      }
      if (filters["category_ids"]) filters["category_ids"] = filters["category_ids"].map(item => Number(item));
      if (filters["channel"]) filters["channel"] = filters.channel.id;
      if (filters["gender"]) filters["gender"] = filters.gender.id;
      if (filters["max_followers"] === 100000) delete filters["max_followers"];
      filteredAdvancedKeys.forEach(key => {
        if (filters[key]) filters[key] = filters[key].map(({ followers, id }) => ({ followers, id }));
      });
      this.setUrlSearchFilters(this.filters);
      this.getInfluencers(filters).then(
        influencers => {
          if (influencers.response.length > 0) {
            this.totalPages = influencers.lastPage;
            this.totalInfluencers = influencers.total;
            this.influencers = influencers.response;
            this.checkFavorite(this.influencers);
          } else {
            this.removeSkeletons();
          }
          this.loading = false;
        },
        error => { console.log(error); this.loading = false; this.removeSkeletons(); }
      );
    },
    checkFavorite(list) {
      list.map(item => {
        const favoriteArray = item.favorite_by_business.map(el => el.uuid);
        if (!this.isAgency) {
          item.is_favorite = favoriteArray.includes(this.user.business.uuid);
        }
        return item;
      });
      this.$forceUpdate();
    },
    setRange(isOpen) {
      if (!isOpen && this.range.length) {
        const min = this.range[0];
        const max = this.range[1];
        const filters = this.cloneDeep(this.filters);
        filters.min_followers = this.ticksNumberRange[min];
        filters.max_followers = this.ticksNumberRange[max];
        this.filters = { ...this.filters, ...filters };
        this.saveFilters();
      }
    },
    selectSorting(item) { if (!item.permission || this.hasPermission(item.permission)) this.selectedSorting = item; },
    cleanFilters(removeAll) {
      const filters = this.cloneDeep(this.filters);
      const filteredKeys = ["age_ranges","channel","gender","max_followers","min_followers","category_ids","age_genders","cities","favorite","has_note","follower_range"];
      if (removeAll) {
        filteredKeys.forEach(key => { if (filters[key] && filters[key].length === 0) delete filters[key]; });
      } else {
        filteredKeys.forEach(key => { if (filters[key]) delete filters[key]; });
        this.range = [0, 5];
      }
      this.filters = filters;
      this.mySelectedCountries = [];
      this.saveFilters();
    },
    applyFollowerFilter(item) {
      if (!item) { delete this.filters.min_followers; delete this.filters.max_followers; }
      else {
        this.filters.min_followers = item.min;
        if (item.max) this.filters.max_followers = item.max;
        else delete this.filters.max_followers;
      }
      this.saveFilters();
    },
    handleInfluencerInvite(influencer) {
      this.selectedInfluencer = null;
      this.$nextTick(() => {
        this.selectedInfluencer = influencer;
        this.showCampaignInvitationDialog = true;
      });
    },
    applyFollowerRange() {
      this.$set(this.filters, 'min_followers', this.ticksNumberRange[this.range[0]]);
      if (this.range[1] < 5) {
        this.$set(this.filters, 'max_followers', this.ticksNumberRange[this.range[1]]);
      } else {
        this.$delete(this.filters, 'max_followers');
      }
      this.followersRange = false;
      this.loading = false;
      this.page = 1;
      this.loadInfluencers(1);
    },
    resetFollowerRange() {
      this.range = [0, 5];
      this.$delete(this.filters, 'min_followers');
      this.$delete(this.filters, 'max_followers');
      this.followersRange = false;
      this.loading = false;
      this.page = 1;
      this.loadInfluencers(1);
    },
    onInfluencerNoteChanged({ uuid, note }) {
      const influencer = this.influencers.find(i => i.uuid === uuid);
      if (influencer) this.$set(influencer, "business_note", note);
    },
  },
  created() {
    this.$root.$on("influencer-note-changed", this.onInfluencerNoteChanged);
    this.handleDebouncedScroll = _.debounce(this.handleScroll, 200);
    window.addEventListener("scroll", this.handleDebouncedScroll);
    if (Object.keys(this.$route.query).length > 0) {
      this.filters = this.cloneDeep(this.getUrlSearchFilters());
      this.page = this.filters.page;
      this.search = this.filters.search ? this.filters.search : null;
      const sort = { orderBy: this.filters.orderBy, [this.filters.order]: true };
      if (!sort.orderBy) this.cleanFilters(true);
      const sorting = this.sortingItems.filter(item => item.orderBy === sort.orderBy && Object.keys(item)[2] === this.filters.order);
      this.selectedSorting = sorting[0];
      if (this.filters.min_followers) {
        this.range = [this.ticksNumberRange.indexOf(this.filters.min_followers), this.ticksNumberRange.indexOf(this.filters.max_followers)];
        if (!this.filters.max_followers) this.range[1] = 5;
      } else {
        this.range = [0, 5];
      }
    }
    this.loadActiveCountries();
    this.loadInfluencers(this.page);
    this.getChannelCategories().then(response => { this.categories = response.data.channelCategories; });
  },
  beforeRouteEnter(to, from, next) { next(vm => { vm.prevRouteName = from.name; }); },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleDebouncedScroll);
    this.$root.$off("influencer-note-changed", this.onInfluencerNoteChanged);
  }
};
</script>

<style lang="scss" scoped>
.pil-wrap { max-width: 1200px; margin: 0 auto; }

/* ── Header ── */
.pil-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  margin-bottom: 20px; gap: 12px; flex-wrap: wrap;
}
.pil-header-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.pil-title { font-size: 22px; font-weight: 700; color: var(--ink-1); margin: 0; }
.pil-subtitle { font-size: 13px; color: var(--ink-3, #aaa); margin: 2px 0 0; }
.pil-header-right { display: flex; align-items: center; gap: 8px; }

.pil-fav-btn {
  display: inline-flex;
  align-items: center;
  padding: 7px 16px;
  border: 1px solid var(--line, #eaecf0);
  border-radius: 8px;
  background: var(--surface, #fff);
  font-size: 13px;
  color: var(--ink-2, #555);
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: var(--surface-2, #f5f5f5); }
  &--active {
    border: 1.5px solid #e05598;
    color: #e05598;
    background: #fdf0f8;
    font-weight: 600;
  }
}

.pil-note-btn--active {
  border: 1.5px solid #e0a526;
  color: #a86b00;
  background: #fff8e6;
  font-weight: 600;
}

/* ── Filters ── */
.pil-filters { margin-bottom: 20px; }

.pil-search-wrap { position: relative; margin-bottom: 12px; }
.pil-search-icon {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  color: var(--ink-3); pointer-events: none;
}
.pil-search {
  width: 100%; height: 40px; padding: 0 12px 0 36px;
  border: 1px solid var(--line); border-radius: 8px;
  background: var(--surface); color: var(--ink-1); font-size: 14px;
  outline: none; font-family: var(--font);
  &:focus { border-color: var(--accent); }
  &::placeholder { color: var(--ink-3); }
}

.pil-chips-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
  margin-bottom: 12px;
}

.pil-chip-select {
  flex: 0 0 auto !important;
  width: auto !important;
  max-width: 130px;

  ::v-deep .v-input__control { min-height: 32px !important; }
  ::v-deep .v-input__slot {
    min-height: 32px !important;
    padding: 0 8px !important;
    border-radius: 8px !important;
    border-color: var(--line) !important;
    background: var(--surface) !important;
    cursor: pointer;
  }
  ::v-deep .v-select__selections {
    font-size: 12px;
    color: var(--ink-1);
    flex-wrap: nowrap;
    padding: 0 !important;
  }
  ::v-deep .v-input__append-inner { margin-top: 4px !important; }
  ::v-deep fieldset { border-radius: 8px !important; }

  /* Active state — coral border + text */
  &.is-active {
    ::v-deep fieldset {
      border-color: #e05540 !important;
      border-width: 1.5px !important;
    }
    ::v-deep .v-select__selections span {
      color: #e05540 !important;
      font-weight: 600;
    }
    ::v-deep .v-input__append-inner .v-icon {
      color: #e05540 !important;
    }
  }
}

.pil-chip-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border: 1px solid var(--line);
  border-radius: 8px;
  font-size: 12px;
  color: var(--ink-2);
  cursor: pointer;
  white-space: nowrap;
  background: var(--surface);
  flex-shrink: 0;
  transition: background 0.15s;
  &:hover { background: var(--surface-2); }
}
.pil-chip-btn--accent {
  color: #e05540;
  border-color: #e05540;
  &:hover { background: rgba(224,85,64,0.06); }
}
.pil-chip-btn--remove {
  color: var(--red, #e05540);
  border-color: var(--red, #e05540);
  &:hover { background: rgba(239,68,68,.06); }
}

.pil-sort-wrap {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.pil-sort-label {
  font-size: 13px;
  color: var(--ink-3, #aaa);
  white-space: nowrap;
}

.pil-total {
  font-size: 13px;
  color: var(--ink-2, #555);
  margin-bottom: 16px;
}

/* ── Grid ── */
.pil-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px;
  @media (max-width: 1100px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 750px)  { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 500px)  { grid-template-columns: 1fr; }
}

.pil-empty {
  text-align: center; padding: 64px 0;
  display: flex; flex-direction: column; align-items: center;
}

/* ── Skeletons ── */
.influencer-skeleton {
  padding: 10px;
  .rounded { border-radius: 999px !important; }
  .influencer-skeleton-buttons { display: flex; justify-content: space-around; width: 100%; }
}

.first-btn { bottom: 70px; }
</style>